import { BalloonText } from '@/components/BalloonText'
import ClearIcon from '@/CustomIcons/ClearIcon'
import PlusIcon from '@/CustomIcons/PlusIcon'
import SendIcon from '@/CustomIcons/SendIcon'
import { Button } from '@/UIComponents2/Button'
import { ControlButton } from '@/UIComponents2/ControlButton'
import Tooltip from '@/UIComponents2/Tooltip'
import { Combobox, createListCollection, Portal, TagsInput, useTagsInput } from '@ark-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Username } from '../Username'
import { useTranslation } from 'react-i18next'

/**
 * This component is AI slop, but it mostly works. Sorry...
 */
type CollaboratorSelectorProps = {
    // If you have a server lookup for usernames
    searchUsers?: (query: string) => Promise<string[]>
    // Local suggestions (e.g., recent collaborators)
    suggestions?: string[]

    onChange?: (collaborators: string[]) => void
    onSubmit?: (collaborators: string[]) => void

    placeholder?: string
    minChars?: number
    maxResults?: number
    maxTags?: number
    disabled?: boolean
    label?: string
}

type UsernameChipProps = {
    onClick?: () => void
    children?: React.ReactNode
}

function UsernameChip(props: UsernameChipProps) {
    return (
        <Button
            className="shadow-glow border-muse-toplit py-[3px] px-[12px] rounded-[18px] t-base-light bg-white/3 hover:bg-white/5 cursor-pointer"
            onClick={props.onClick}
        >
            <span className="flex items-center gap-[6px]">{props.children}</span>
        </Button>
    )
}

function normalizeUsername(raw: string) {
    const v = (raw || '').trim()
    if (!v) return ''
    // strip leading '@', normalize case for dedupe. Display can still render with '@'.
    return v.replace(/^@+/, '').toLowerCase()
}

export default function CollaboratorSelector({
    searchUsers,
    suggestions = [],
    onChange,
    onSubmit,
    placeholder,
    minChars = 1,
    maxResults = 10,
    maxTags = 50,
    disabled = false,
    label,
}: CollaboratorSelectorProps) {
    const { t } = useTranslation()
    const tags = useTagsInput()
    const [sent, setSent] = useState(false)
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [loading, setLoading] = useState(false)
    const [remote, setRemote] = useState<string[]>([])
    const lastReqId = useRef(0)
    const invitedUsers = useRef<string[]>([])

    // Call onChange when tags updated
    useEffect(() => {
        if (onChange) onChange(tags.value)
    }, [tags.value, onChange])

    // Debounced remote search
    useEffect(() => {
        if (!searchUsers) {
            setRemote([])
            return
        }
        const q = normalizeUsername(query)
        if (q.length < minChars) {
            setRemote([])
            return
        }
        let alive = true
        const reqId = ++lastReqId.current
        setLoading(true)
        const t = setTimeout(async () => {
            try {
                const res = await searchUsers(q)
                if (!alive || reqId !== lastReqId.current) return
                // Allow raw values; normalize only for dedupe
                setRemote(Array.isArray(res) ? res : [])
            } catch {
                if (!alive || reqId !== lastReqId.current) return
                setRemote([])
            } finally {
                if (alive && reqId === lastReqId.current) setLoading(false)
            }
        }, 150)
        return () => {
            alive = false
            clearTimeout(t)
        }
    }, [query, minChars, searchUsers])

    const selectedSet = useMemo(() => {
        const s = new Set<string>()
        for (const v of tags.value) s.add(normalizeUsername(v))
        return s
    }, [tags.value])

    const invitedSet = useMemo(
        () => new Set(invitedUsers.current.map((u) => normalizeUsername(u ?? ''))),
        [invitedUsers.current.length]
    )

    const filteredOptions = useMemo(() => {
        const qn = normalizeUsername(query)
        const source = [
            ...remote,
            ...suggestions, // remote first, then local
        ]

        const uniq: string[] = []
        const seen = new Set<string>()
        for (const raw of source) {
            const n = normalizeUsername(raw)
            if (!n) continue
            if (seen.has(n)) continue
            if (selectedSet.has(n)) continue
            if (invitedSet.has(n)) continue
            if (qn && !n.includes(qn)) continue
            uniq.push(raw)
            seen.add(n)
            if (uniq.length >= maxResults) break
        }
        return uniq
    }, [query, remote, suggestions, selectedSet, invitedSet, maxResults])

    const collection = useMemo(() => createListCollection({ items: filteredOptions }), [filteredOptions])

    const commitTag = useCallback(
        (raw: string) => {
            const n = normalizeUsername(raw)
            if (!n) return false
            if (selectedSet.has(n)) {
                // Already selected
                tags.setInputValue('')
                setQuery('')
                setOpen(false)
                return false
            }
            if (tags.value.length >= maxTags) return false
            // Commit lowercased normalized username for consistency.
            // If you prefer preserving original case from results,
            // pass that display value instead of 'n'.
            tags.addValue(n)
            tags.setInputValue('')
            setQuery('')
            setOpen(false)
            return true
        },
        [selectedSet, tags, maxTags]
    )

    const handleSubmit = useCallback(() => {
        if (!onSubmit) return
        const pending = normalizeUsername(tags.inputValue)
        if (pending) commitTag(pending)
        const final = [...tags.value]
        if (final.length === 0) return

        onSubmit(final)
        setSent(true)
        setTimeout(() => setSent(false), 700)

        tags.clearValue()
        tags.setInputValue('')
        invitedUsers.current = invitedUsers.current.concat(final)
    }, [onSubmit, tags, commitTag])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const key = e.key

            // Submit all tags
            if ((key === 'Enter' || key === 'NumpadEnter') && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                handleSubmit()
                return
            }

            // Backspace to remove last tag when input empty
            if (key === 'Backspace' && !tags.inputValue && tags.value.length > 0) {
                e.preventDefault()
                tags.setValue(tags.value.slice(0, tags.value.length - 1))
                return
            }

            // Escape to close combobox or clear input
            if (key === 'Escape') {
                if (open) {
                    // e.preventDefault()
                    // setOpen(false)
                } else if (tags.inputValue) {
                    e.preventDefault()
                    tags.setInputValue('')
                    setQuery('')
                }
                return
            }

            // Commit with Enter when combobox is closed
            if ((key === 'Enter' || key === 'NumpadEnter') && !open && tags.inputValue) {
                e.preventDefault()
                commitTag(tags.inputValue)
                return
            }

            // Tab to commit if combobox closed; otherwise default behavior
            if (key === 'Tab' && tags.inputValue) {
                e.preventDefault()
                if (open) {
                    const enterEvent = new KeyboardEvent('keydown', {
                        key: 'Enter',
                        code: 'Enter',
                        keyCode: 13,
                        which: 13,
                        bubbles: true,
                    })
                    e.target.dispatchEvent(enterEvent)
                } else {
                    commitTag(tags.inputValue)
                }
                return
            }

            // Space or comma to commit when combobox closed
            if ((key === ' ' || key === ',') && tags.inputValue?.trim()) {
                e.preventDefault()
                commitTag(tags.inputValue)
                return
            }
        },
        [open, tags, commitTag, handleSubmit]
    )

    const gridStyle = onSubmit ? 'w-full grid grid-cols-[1fr_min-content] gap-[8px]' : 'w-full grid grid-cols-1'

    return (
        <div className="w-full grow">
            <TagsInput.RootProvider value={tags}>
                <TagsInput.Context>
                    {(ctx) => (
                        <div className="w-full flex flex-col gap-2">
                            {label && <TagsInput.Label>{label}</TagsInput.Label>}
                            <div className={gridStyle}>
                                <div className="min-w-0 flex flex-wrap items-center content-center w-full bg-white/3 rounded-[12px] border-muse-toplit px-[15px] shadow-glow focus-within:outline-2 outline-muse-yellow/60">
                                    <TagsInput.Control className="min-w-0 flex gap-[4px] flex-wrap pt-2">
                                        {ctx.value.map((value, index) => (
                                            <TagsInput.Item key={`${value}-${index}`} index={index} value={value}>
                                                <TagsInput.ItemPreview>
                                                    <TagsInput.ItemDeleteTrigger
                                                        aria-label={`Remove @${value}`}
                                                        asChild
                                                    >
                                                        <UsernameChip>
                                                            <Username username={value} />{' '}
                                                            <ClearIcon className="opacity-60" />
                                                        </UsernameChip>
                                                    </TagsInput.ItemDeleteTrigger>
                                                </TagsInput.ItemPreview>
                                                <TagsInput.ItemInput />
                                            </TagsInput.Item>
                                        ))}
                                    </TagsInput.Control>
                                    <Combobox.Root
                                        collection={collection}
                                        allowCustomValue
                                        closeOnSelect
                                        inputBehavior="autohighlight"
                                        onOpenChange={(e) => {
                                            if (
                                                !e.open &&
                                                e.reason === 'item-select' &&
                                                filteredOptions.length === 0 &&
                                                query.length > 0
                                            ) {
                                                const v = query.trim()
                                                if (v) {
                                                    commitTag(v)
                                                }
                                            }
                                            setOpen(e.open)
                                        }}
                                        composite
                                        value={tags.value}
                                        onInputValueChange={(details) => {
                                            if (disabled) return
                                            const v = details.inputValue ?? ''
                                            ctx.setInputValue(v)
                                            setQuery(v)
                                            setOpen(v.trim().length >= minChars)
                                        }}
                                        onValueChange={(details: any) => {
                                            const v = String(details?.value ?? '').trim()
                                            if (v) commitTag(v)
                                        }}
                                        className="w-full"
                                    >
                                        <Combobox.Control onKeyDown={handleKeyDown}>
                                            <Combobox.Input asChild>
                                                <TagsInput.Input
                                                    disabled={disabled}
                                                    placeholder={placeholder ?? t('Enter username')}
                                                    className="w-full outline-none py-[8px] placeholder-[rgba(255,255,255,0.3)]"
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                    aria-autocomplete="list"
                                                    aria-expanded={open}
                                                />
                                            </Combobox.Input>
                                        </Combobox.Control>
                                        <Portal>
                                            {open && (filteredOptions.length > 0 || loading) && (
                                                <Combobox.Positioner>
                                                    <Combobox.Content className="bg-muse-menu shadow-glow border-muse-toplit-low rounded-[16px] py-4 min-w-[240px] z-30">
                                                        <Combobox.ItemGroup>
                                                            {loading && (
                                                                <div className="px-4 py-1 text-sm opacity-70">
                                                                    Searching…
                                                                </div>
                                                            )}
                                                            {filteredOptions.map((item) => (
                                                                <Combobox.Item
                                                                    key={item}
                                                                    item={item}
                                                                    className="px-4 py-1 cursor-pointer flex items-center gap-2 data-[highlighted]:bg-white/20"
                                                                >
                                                                    <Combobox.ItemText>{item}</Combobox.ItemText>
                                                                    <Combobox.ItemIndicator>✓</Combobox.ItemIndicator>
                                                                </Combobox.Item>
                                                            ))}
                                                        </Combobox.ItemGroup>
                                                    </Combobox.Content>
                                                </Combobox.Positioner>
                                            )}
                                        </Portal>
                                    </Combobox.Root>
                                </div>

                                {onSubmit && (
                                    <Tooltip text="Send Invitation">
                                        <ControlButton
                                            variant="primary"
                                            className="relative self-start overflow-visible max-h-[36px]"
                                            onClick={handleSubmit}
                                            disabled={disabled}
                                            aria-label="Send invitations"
                                        >
                                            <SendIcon className="text-black" />
                                            <BalloonText active={sent} text="Sent!" />
                                        </ControlButton>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Suggestion chips */}
                            <div className="w-full flex gap-[8px] flex-wrap">
                                {suggestions
                                    .filter((u) => {
                                        const n = normalizeUsername(u)
                                        return n && !selectedSet.has(n) && !invitedSet.has(n)
                                    })
                                    .slice(0, 6)
                                    .map((username) => (
                                        <UsernameChip key={username} onClick={() => commitTag(username)}>
                                            <PlusIcon /> <Username username={username} />
                                        </UsernameChip>
                                    ))}
                            </div>
                        </div>
                    )}
                </TagsInput.Context>
                <TagsInput.HiddenInput />
            </TagsInput.RootProvider>
        </div>
    )
}
